import type { CreatorUgcOffer, PlatformEnum } from '@fluenceur/types';
import { useInvitationStoreSelector } from '@/contexts/invitation-context';
import { useCollabUgcTermsQuery } from '@/hooks/queries/use-collab-ugc-terms-query';
import { useResolveContactQuery } from '@/hooks/queries/use-resolve-contact-query';
import { useCollabRecipients } from './use-collab-recipients';

/**
 * Whether every recipient of the wizard offers UGC, read from the ugc-terms
 * endpoint the send applies, so the type step preselects UGC whichever entry
 * opened it (Discovery, wishlist, collab tracking), not only the contact sheet
 * that seeds the offer in the store. A Discovery creator arrives as a social
 * profile with no identity yet: she is resolved through the shared contact
 * cache first. Asked only while `enabled`.
 *
 * `monoOffer` is the offer itself when the wizard proposes to one creator, so
 * the type step can state her starting price; null for several creators.
 */
export const useRecipientUgcOffer = (
  enabled: boolean
): { offersUgc: boolean; monoOffer: CreatorUgcOffer | null } => {
  const recipients = useCollabRecipients();
  const creator = useInvitationStoreSelector((state) => state.creator);
  const knownIds = recipients.flatMap((recipient) =>
    recipient.creatorIdentityId ? [recipient.creatorIdentityId] : []
  );
  const resolveMono =
    enabled && recipients.length === 1 && knownIds.length === 0 && !!creator?.username;
  const { data: resolved } = useResolveContactQuery(
    resolveMono && creator
      ? { entryType: 'creator', id: creator.username, platform: creator.platform as PlatformEnum }
      : null
  );
  const ids =
    knownIds.length > 0
      ? knownIds
      : resolveMono && resolved?.creatorIdentityId
        ? [resolved.creatorIdentityId]
        : [];
  const { data: terms } = useCollabUgcTermsQuery(ids, enabled);

  const offersUgc =
    enabled &&
    ids.length > 0 &&
    ids.every((id) =>
      terms?.some((term) => term.creatorIdentityId === id && term.ugcOffer !== null)
    );
  const monoOffer =
    offersUgc && recipients.length === 1
      ? (terms?.find((term) => term.creatorIdentityId === ids[0])?.ugcOffer ?? null)
      : null;

  return { offersUgc, monoOffer };
};
